/**
 * Regular expression to match geo-point coordinates in the format:
 * latitude, longitude (e.g., "-26.195547, 28.014446")
 *
 * Named capture groups:
 * - lat: latitude coordinate
 * - lng: longitude coordinate
 */
export const GEO_POINT_REGEX = /(?<lat>-?\d+(?:\.\d+)?)[,\s]+(?<lng>-?\d+(?:\.\d+)?)/

/**
 * Check if an address string is a geo-point coordinate
 */
export const isGeoPoint = (address: string): boolean => GEO_POINT_REGEX.test(address.trim())

/**
 * Determine if the address suggestions dropdown should be shown based on address input state
 */
export const shouldShowAddressSuggestions = (params: {
  address: string
  hasTypedEnough: boolean
  selectedSuggestion: unknown
  isEditMode: boolean
  originalAddress?: string
}): boolean => {
  const { address, hasTypedEnough, selectedSuggestion, isEditMode, originalAddress } = params
  const trimmed = address.trim()
  const len = trimmed.length
  const unchangedInEdit = isEditMode && trimmed === (originalAddress ?? '').trim()
  const isGeo = isGeoPoint(trimmed)

  // Show if enough characters or geo-point, no selection, and not unchanged in edit mode
  if ((len >= 3 || isGeo) && !selectedSuggestion && (!isEditMode || !unchangedInEdit)) {
    return true
  }
  // Show if user has typed enough before but then deleted characters
  if (len < 3 && hasTypedEnough && !isGeo) {
    return true
  }
  return false
}

/**
 * Determine if the address suggestions dropdown should be shown on focus
 */
export const shouldShowSuggestionsOnFocus = (params: {
  address: string
  suggestionsLoading: boolean
  hasSuggestions: boolean
  hasError: boolean
  selectedSuggestion: unknown
  isEditMode: boolean
  originalAddress?: string
}): boolean => {
  const {
    address,
    suggestionsLoading,
    hasSuggestions,
    hasError,
    selectedSuggestion,
    isEditMode,
    originalAddress,
  } = params
  const trimmed = address.trim()
  const len = trimmed.length
  const isGeo = isGeoPoint(trimmed)

  return (
    ((len >= 3 && (suggestionsLoading || hasSuggestions || hasError)) || isGeo) &&
    !selectedSuggestion &&
    (!isEditMode || trimmed !== (originalAddress ?? '').trim())
  )
}
